import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { PlatformSummary } from "@codestack/shared";
import { createPlatformSchema } from "@codestack/shared";
import { Badge, Button, Input, Modal, Select, StatusBadge, Textarea } from "@codestack/ui";
import { apiRequest, ApiError } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { EmptyState, ErrorState, InlineAlert, LoadingState } from "../components/States";
import { dateOnly, money, percent } from "../lib/format";

type CreatedCredentials = {
  platform: { id: string; name: string; slug: string };
  credentials: { keyPrefix: string; secret: string };
};

type SelfTestCheck = {
  id: string;
  label: string;
  status: "ok" | "warn" | "fail";
  detail: string;
};

type SelfTestResult = {
  ok: boolean;
  summary: string;
  status: number | null;
  latencyMs: number | null;
  checks: SelfTestCheck[];
  testedAt: string;
};

const CHECK_TONE: Record<SelfTestCheck["status"], "success" | "warning" | "danger"> = {
  ok: "success",
  warn: "warning",
  fail: "danger",
};

type FormState = {
  name: string;
  slug: string;
  description: string;
  domain: string;
  callbackUrl: string;
  status: "ACTIVE" | "SUSPENDED" | "ARCHIVED";
  showOnHomepage: boolean;
  feePercent: string;
};

const emptyForm: FormState = {
  name: "",
  slug: "",
  description: "",
  domain: "",
  callbackUrl: "",
  status: "ACTIVE",
  showOnHomepage: true,
  feePercent: "2.5",
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function PlatformsPage() {
  const { can } = useAuth();
  const { data, error, loading, reload } = useApi<PlatformSummary[]>("/admin/platforms");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<CreatedCredentials | null>(null);

  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, SelfTestResult>>({});
  const [testErrors, setTestErrors] = useState<Record<string, string>>({});

  const [deleteTarget, setDeleteTarget] = useState<PlatformSummary | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const canWrite = can("platform:write");

  const openDelete = (platform: PlatformSummary) => {
    setDeleteTarget(platform);
    setDeleteConfirm("");
    setDeleteError(null);
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteConfirm("");
    setDeleteError(null);
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiRequest<void>(`/admin/platforms/${deleteTarget.id}`, {
        method: "DELETE",
        body: { confirmSlug: deleteConfirm.trim() },
      });
      setDeleteTarget(null);
      setDeleteConfirm("");
      reload();
    } catch (cause) {
      setDeleteError(
        cause instanceof ApiError ? cause.message : "The platform could not be deleted.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const runSelfTest = async (platformId: string) => {
    setTestingId(platformId);
    setTestErrors((current) => {
      const next = { ...current };
      delete next[platformId];
      return next;
    });
    try {
      const result = await apiRequest<SelfTestResult>(`/admin/platforms/${platformId}/test`, {
        method: "POST",
      });
      setTestResults((current) => ({ ...current, [platformId]: result }));
    } catch (cause) {
      setTestErrors((current) => ({
        ...current,
        [platformId]:
          cause instanceof ApiError ? cause.message : "The self-test could not be run.",
      }));
    } finally {
      setTestingId(null);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data ?? []).filter((platform) => {
      const matchesTerm =
        !term ||
        platform.name.toLowerCase().includes(term) ||
        platform.slug.toLowerCase().includes(term) ||
        (platform.domain ?? "").toLowerCase().includes(term);
      const matchesStatus = !statusFilter || platform.status === statusFilter;
      return matchesTerm && matchesStatus;
    });
  }, [data, search, statusFilter]);

  const openCreate = () => {
    setForm(emptyForm);
    setFieldErrors({});
    setFormError(null);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreated(null);
  };

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const parsed = createPlatformSchema.safeParse({
      name: form.name.trim(),
      slug: form.slug.trim() || slugify(form.name),
      description: form.description.trim() || undefined,
      domain: form.domain.trim() || undefined,
      callbackUrl: form.callbackUrl.trim() || undefined,
      status: form.status,
      showOnHomepage: form.showOnHomepage,
      feePercent: Number(form.feePercent),
    });

    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!errors[key]) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const result = await apiRequest<CreatedCredentials>("/admin/platforms", {
        method: "POST",
        body: parsed.data,
      });
      setCreated(result);
      reload();
    } catch (cause) {
      setFormError(
        cause instanceof ApiError ? cause.message : "The platform could not be created.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-xl font-bold text-white">Platforms</h1>
          <p className="text-sm text-ink-400">
            Every product connected to the CodeStack payment gateway.
          </p>
        </div>
        {canWrite ? <Button onClick={openCreate}>New platform</Button> : null}
      </div>

      <div className="admin-card grid gap-3 p-4 sm:grid-cols-[1fr_200px]">
        <Input
          placeholder="Search by name, slug or domain"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="ARCHIVED">Archived</option>
        </Select>
      </div>

      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {loading && !data ? <LoadingState label="Loading platforms…" /> : null}

      {data && filtered.length === 0 ? (
        <EmptyState
          title={data.length === 0 ? "No platforms yet" : "No platforms match your filters"}
          description={
            data.length === 0
              ? "Create your first platform to issue API credentials and start accepting payments."
              : "Try a different search term or clear the status filter."
          }
          action={
            data.length === 0 && canWrite ? (
              <Button size="sm" onClick={openCreate}>
                Create platform
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {filtered.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((platform) => (
            <div
              key={platform.id}
              className="admin-card group flex flex-col p-5 transition-colors hover:border-white/20 hover:bg-white/[0.04]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-display text-base font-semibold text-white">
                    {platform.name}
                  </p>
                  <p className="mt-0.5 truncate font-mono text-[11px] text-ink-500">
                    {platform.slug}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <StatusBadge status={platform.status} />
                  {platform.showOnHomepage ? null : (
                    <Badge tone="neutral">Hidden from homepage</Badge>
                  )}
                </div>
              </div>

              <p className="mt-3 line-clamp-2 min-h-[2.5rem] text-xs leading-relaxed text-ink-400">
                {platform.description ?? "No description provided."}
              </p>

              <dl className="mt-4 grid grid-cols-3 gap-2 border-t border-white/[0.06] pt-4">
                <div>
                  <dt className="admin-label">Volume</dt>
                  <dd className="mt-1 font-mono text-xs text-ink-100">
                    {money(platform.stats?.totalVolumeMinor ?? "0", "NGN")}
                  </dd>
                </div>
                <div>
                  <dt className="admin-label">Txns</dt>
                  <dd className="mt-1 font-mono text-xs text-ink-100">
                    {platform.stats?.transactionCount ?? 0}
                  </dd>
                </div>
                <div>
                  <dt className="admin-label">Success</dt>
                  <dd className="mt-1 font-mono text-xs text-ink-100">
                    {percent(platform.stats?.successRate ?? 0, 0)}
                  </dd>
                </div>
              </dl>

              <div className="mt-4 flex items-center justify-between text-[11px] text-ink-600">
                <span className="truncate">{platform.domain ?? "No domain"}</span>
                <Badge tone="brand">{platform.feePercent}% fee</Badge>
              </div>

              {canWrite ? (
                <div className="mt-4 border-t border-white/[0.06] pt-3">
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={testingId === platform.id}
                      onClick={() => runSelfTest(platform.id)}
                    >
                      {testingId === platform.id ? "Testing…" : "Test API"}
                    </Button>
                    <div className="flex items-center gap-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openDelete(platform)}
                      >
                        Delete
                      </Button>
                      <Link
                        to={`/platforms/${platform.id}`}
                        className="text-[11px] font-medium text-brand-300 hover:text-brand-200"
                      >
                        Manage →
                      </Link>
                    </div>
                  </div>

                  {testErrors[platform.id] ? (
                    <p className="mt-2 text-[11px] text-rose-300">{testErrors[platform.id]}</p>
                  ) : null}

                  {testResults[platform.id] ? (
                    <div className="mt-2 space-y-1.5">
                      <p className="text-[11px] text-ink-400">
                        {testResults[platform.id]?.summary}
                      </p>
                      <ul className="space-y-1">
                        {(testResults[platform.id]?.checks ?? []).map((check) => (
                          <li key={check.id} className="flex items-start gap-1.5">
                            <Badge tone={CHECK_TONE[check.status]} className="mt-0.5 shrink-0">
                              {check.status === "ok"
                                ? "OK"
                                : check.status === "warn"
                                  ? "Warn"
                                  : "Fail"}
                            </Badge>
                            <span className="text-[11px] text-ink-500">{check.label}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <Modal
        open={createOpen}
        onClose={closeCreate}
        title={created ? "Platform created" : "Create platform"}
        description={
          created
            ? "Store these credentials now — the secret is shown only once."
            : "Issue a new set of API credentials for a connected product."
        }
        size="lg"
        footer={
          created ? (
            <Button onClick={closeCreate}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={closeCreate} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" form="create-platform-form" disabled={submitting}>
                {submitting ? "Creating…" : "Create platform"}
              </Button>
            </>
          )
        }
      >
        {created ? (
          <div className="space-y-4">
            <InlineAlert tone="warning">
              The secret key cannot be retrieved again. Copy it into the platform's
              environment before closing this dialog.
            </InlineAlert>
            <div className="space-y-3">
              <div>
                <p className="admin-label">Key prefix</p>
                <code className="mt-1 block break-all rounded-lg bg-ink-950 px-3 py-2 font-mono text-xs text-brand-200">
                  {created.credentials.keyPrefix}
                </code>
              </div>
              <div>
                <p className="admin-label">Secret key</p>
                <code className="mt-1 block break-all rounded-lg bg-ink-950 px-3 py-2 font-mono text-xs text-amber-200">
                  {created.credentials.secret}
                </code>
              </div>
            </div>
            <p className="text-xs text-ink-500">
              Platform <span className="text-ink-300">{created.platform.name}</span> is ready.
              Configure its callback URL and settings from the platform detail page.
            </p>
          </div>
        ) : (
          <form id="create-platform-form" onSubmit={submit} className="space-y-4">
            {formError ? <InlineAlert tone="danger">{formError}</InlineAlert> : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Name"
                value={form.name}
                error={fieldErrors.name}
                onChange={(event) => {
                  const value = event.target.value;
                  setForm((current) => ({
                    ...current,
                    name: value,
                    slug:
                      current.slug === "" || current.slug === slugify(current.name)
                        ? slugify(value)
                        : current.slug,
                  }));
                }}
                placeholder="CodeStack Cloud"
                required
              />
              <Input
                label="Slug"
                value={form.slug}
                error={fieldErrors.slug}
                onChange={(event) => update("slug", slugify(event.target.value))}
                placeholder="codestack-cloud"
                hint="Used in API references and URLs."
                required
              />
            </div>

            <Textarea
              label="Description"
              value={form.description}
              error={fieldErrors.description}
              onChange={(event) => update("description", event.target.value)}
              rows={3}
              placeholder="What this platform does."
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Domain"
                value={form.domain}
                error={fieldErrors.domain}
                onChange={(event) => update("domain", event.target.value)}
                placeholder="numbaport.com"
                hint="Bare domain is fine — no https:// needed."
              />
              <Input
                label="Callback URL"
                value={form.callbackUrl}
                error={fieldErrors.callbackUrl}
                onChange={(event) => update("callbackUrl", event.target.value)}
                placeholder="https://cloud.codestacktechnologies.com/webhooks/payments"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Status"
                value={form.status}
                onChange={(event) =>
                  update("status", event.target.value as FormState["status"])
                }
              >
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="ARCHIVED">Archived</option>
              </Select>
              <Input
                label="Fee percent"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.feePercent}
                error={fieldErrors.feePercent}
                onChange={(event) => update("feePercent", event.target.value)}
                hint="Platform fee applied to successful payments."
              />
            </div>

            <label className="flex items-start gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-ink-950 accent-brand-500"
                checked={form.showOnHomepage}
                onChange={(event) => update("showOnHomepage", event.target.checked)}
              />
              <span className="min-w-0">
                <span className="block text-xs font-medium text-ink-200">
                  Show on homepage
                </span>
                <span className="mt-0.5 block text-[11px] text-ink-500">
                  List this platform in the public directory. You can change this later from
                  the platform's detail page.
                </span>
              </span>
            </label>
          </form>
        )}
      </Modal>

      <Modal
        open={deleteTarget !== null}
        onClose={closeDelete}
        title="Delete platform"
        description="This permanently removes the platform and everything attached to it."
        footer={
          <>
            <Button variant="ghost" onClick={closeDelete} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={remove}
              disabled={deleting || deleteConfirm.trim() !== deleteTarget?.slug}
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </Button>
          </>
        }
      >
        {deleteTarget ? (
          <div className="space-y-4">
            {deleteError ? <InlineAlert tone="danger">{deleteError}</InlineAlert> : null}
            <InlineAlert tone="warning">
              Deleting <span className="font-semibold">{deleteTarget.name}</span> also deletes its
              API credentials, settings, customers, orders, payments, refunds, settlements and
              ledger entries. This cannot be undone.
            </InlineAlert>
            <p className="text-sm text-ink-400">
              {deleteTarget.stats?.transactionCount
                ? `This platform has ${deleteTarget.stats.transactionCount} transaction${
                    deleteTarget.stats.transactionCount === 1 ? "" : "s"
                  } on record. Export them first if you need them for accounting.`
                : "This platform has no transactions on record."}
            </p>
            <Input
              label="Confirm the slug to continue"
              value={deleteConfirm}
              onChange={(event) => setDeleteConfirm(event.target.value)}
              placeholder={deleteTarget.slug}
              hint={`Type ${deleteTarget.slug} exactly.`}
              autoFocus
              disabled={deleting}
            />
          </div>
        ) : null}
      </Modal>

      {data && data.length > 0 ? (
        <p className="text-[11px] text-ink-600">
          {data.length} platform{data.length === 1 ? "" : "s"} · oldest created{" "}
          {dateOnly(data[data.length - 1]?.createdAt)}
        </p>
      ) : null}
    </div>
  );
}
